import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import ApiClient from "#/redux/api/ApiClient";
import { handleLoadingAndErrorState } from "#/redux/util/reduxUtils";
import { getErrorMessage } from "#/utils/APIUtils";

const NAME = "authorization";
const baseUrl = "/api/varn_shema/zastopnistvo";

function normalizePayload(payload) {
  // relic, will be removed
  // for now, we only do this to satisft NOT_NULL constraint
  if (!("lahkoDodeljujeNaprej" in payload)) {
    payload.lahkoDodeljujeNaprej = false;
  }
}

// =============================================================================
// ASYNC THUNKS
// =============================================================================
export const fetchAuthorizationPage = createAsyncThunk(
  `${NAME}/fetchPage`,
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      return ApiClient.get(
        `${baseUrl}/page`,
        undefined,
        page,
        filter,
        sort,
        false
      ).then(({ data }) => data);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchAuthorizationById = createAsyncThunk(
  `${NAME}/fetchById`,
  async (id, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(`${baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const createAuthorization = createAsyncThunk(
  `${NAME}/create`,
  async ({ payload }, { rejectWithValue }) => {
    try {
      normalizePayload(payload);

      const response = await ApiClient.post(baseUrl, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateAuthorization = createAsyncThunk(
  `${NAME}/update`,
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      normalizePayload(payload);

      const response = await ApiClient.put(`${baseUrl}/${id}`, payload);
      return { id, data: response.data };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deleteAuthorization = createAsyncThunk(
  `${NAME}/delete`,
  async ({ id, version }, { rejectWithValue }) => {
    try {
      await ApiClient.delete(`${baseUrl}/${id}`, { version });
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * Careful here - this is doing additional processing on top of fetchPage, so it is not a simple wrapper.
 * It is fetching the same data, but then transforming it into a different shape (grouping by user and aggregating roles).
 * This is used in the SelectUser component to show users with their associated roles.
 * If you just want to fetch the raw page of zastopnistva, use fetchAuthorizationPage instead.
 * @returns
 */
export const fetchUsersWithRoles = createAsyncThunk(
  `${NAME}/fetchUsersWithRoles`,
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const data = await ApiClient.get(
        `${baseUrl}/page`,
        undefined,
        page,
        filter,
        sort,
        false
      ).then(({ data: aux }) => aux);

      const list = data?.content || [];

      // Group by user ID
      const userMap = new Map();
      list.forEach(({ uporabnik, nivoDostopa }) => {
        if (uporabnik?.id) {
          const userId = uporabnik.id;
          if (!userMap.has(userId)) {
            userMap.set(userId, {
              id: userId,
              uporabnik: uporabnik,
              roles: [],
            });
          }
          if (nivoDostopa) {
            userMap.get(userId).roles.push(nivoDostopa);
          }
        }
      });

      const groupedUsers = Array.from(userMap.values());

      return {
        content: groupedUsers,
        totalElements: groupedUsers.length,
        totalPages: 1,
        size: groupedUsers.length,
        number: 0,
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const authorizationSlice = createSlice({
  name: NAME,
  initialState: {
    isLoading: false,
    error: null,

    page: {},

    // this is a special case - we are storing users with their
    // associated roles here for easy access in the SelectUser component
    usersWithRoles: [],
  },

  extraReducers: (builder) => {
    const thunks = [
      fetchAuthorizationPage,
      fetchUsersWithRoles,
      fetchAuthorizationById,
      createAuthorization,
      updateAuthorization,
      deleteAuthorization,
    ];

    builder
      .addCase(fetchAuthorizationPage.fulfilled, (state, action) => {
        state.page = action.payload;
      })
      .addCase(fetchUsersWithRoles.fulfilled, (state, { payload = [] }) => {
        state.usersWithRoles = payload;
      });

    handleLoadingAndErrorState(builder, thunks);
  },
});

/**
 * Authorization setter and clearer actions.
 */
export const { setAuthorization, clearAuthorization } =
  authorizationSlice.actions;

export function currentAuthorization(state) {
  return state.current;
}

/**
 * Authorization slice reducer.
 */
export default authorizationSlice.reducer;
